import { useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Watchable } from '@cotera/client/v0/actions/framework/watchable';

export type UrlParamAdapter = {
  setParam: (key: string, value: string) => void;
  /** Set multiple params in one navigation so they don't overwrite each other. */
  setParams: (params: Record<string, string>) => void;
};

export type UrlParamWatchableOptions<T> = {
  default: T;
  parse: (s: string) => T;
  serialize: (t: T) => string;
  equalityFn?: (a: T, b: T) => boolean;
};

export type UrlParamWatchableMultiOptions<T> = {
  default: T;
  parse: (params: URLSearchParams) => T;
  serialize: (t: T) => Record<string, string>;
  equalityFn?: (a: T, b: T) => boolean;
};

/**
 * A watchable that syncs its state with a single key in the URL query string.
 * - set(value) writes the serialized value to the URL and updates the watchable.
 * - syncFromUrl(params) updates the watchable from the current URL (e.g. after back/forward).
 * The adapter is responsible for reading/writing the URL (e.g. via React Router's setSearchParams).
 */
export class UrlParamWatchable<T> {
  readonly key: string;
  private readonly _watchable: Watchable<T>;
  private readonly _adapter: UrlParamAdapter;
  private readonly _parse: (s: string) => T;
  private readonly _serialize: (t: T) => string;

  constructor(
    key: string,
    options: UrlParamWatchableOptions<T>,
    adapter: UrlParamAdapter,
    initialValue: T
  ) {
    this.key = key;
    this._adapter = adapter;
    this._parse = options.parse;
    this._serialize = options.serialize;
    this._watchable = Watchable.fromValue(initialValue, {
      equalityFn: options.equalityFn,
      updater: (newVal) => {
        this._adapter.setParam(this.key, this._serialize(newVal));
        return newVal;
      },
    });
  }

  snapshot(): T {
    return this._watchable.snapshot();
  }

  /** Updates the watchable and syncs the serialized value to the URL (via the watchable's updater). */
  set(value: T): void {
    this._watchable.set(value);
  }

  /**
   * Sync the watchable from the current URL (e.g. after browser back/forward).
   * Call this from a component effect when searchParams change.
   * Uses setFromSource so we do not trigger the updater (no redundant URL write).
   */
  syncFromUrl(params: URLSearchParams): void {
    const raw = params.get(this.key);
    if (raw !== null && raw !== '') {
      try {
        const parsed = this._parse(raw);
        this._watchable.setFromSource(parsed);
      } catch {
        // ignore invalid stored value
      }
    }
  }

  /** Expose as ReadonlyWatchable for useWatchableValue etc. */
  asWatchable(): Watchable<T> {
    return this._watchable;
  }
}

/**
 * A watchable that syncs an object with multiple URL query keys in one go.
 * set(value) writes all serialized entries to the URL in a single navigation.
 * Use this when multiple params must stay in sync (e.g. search + page) so one
 * set() doesn't get overwritten by another.
 */
export class MultiKeyUrlParamWatchable<T> {
  private readonly _watchable: Watchable<T>;
  private readonly _adapter: UrlParamAdapter;
  private readonly _parse: (params: URLSearchParams) => T;
  private readonly _serialize: (t: T) => Record<string, string>;
  private _disposed = false;

  constructor(
    _keys: string[],
    options: UrlParamWatchableMultiOptions<T>,
    adapter: UrlParamAdapter,
    initialValue: T
  ) {
    this._adapter = adapter;
    this._parse = options.parse;
    this._serialize = options.serialize;
    this._watchable = Watchable.fromValue(initialValue, {
      equalityFn: options.equalityFn,
      updater: (newVal) => {
        this._adapter.setParams(this._serialize(newVal));
        return newVal;
      },
    });
  }

  snapshot(): T {
    return this._watchable.snapshot();
  }

  /** Updates the watchable and syncs all params to the URL (via the watchable's updater). */
  set(value: T): void {
    if (this._disposed) {
      return;
    }
    this._watchable.set(value);
  }

  /**
   * Sync the watchable from the current URL (e.g. after browser back/forward).
   * Uses setFromSource so we do not trigger the updater (no redundant URL write).
   */
  syncFromUrl(params: URLSearchParams): void {
    if (this._disposed) {
      return;
    }
    try {
      const parsed = this._parse(params);
      this._watchable.setFromSource(parsed);
    } catch {
      // ignore invalid stored values
    }
  }

  /** Stops accepting updates and URL sync; call when the owning model is disposed. */
  dispose(): void {
    this._disposed = true;
  }

  asWatchable(): Watchable<T> {
    return this._watchable;
  }
}

/**
 * Returns a stable UrlParamAdapter that updates the URL via navigate (current pathname + new search).
 * Uses a ref for location so the adapter identity does not change when the URL changes; otherwise
 * consumers that put the adapter in dependency arrays (e.g. ModelScopeFactory deps) would recreate
 * on every param change and cause feedback loops (setParams -> URL change -> new adapter -> models recreated).
 * Use in pages that need to sync list params (page, limit, search, sort) to the URL.
 */
export function useUrlAdapter(): UrlParamAdapter {
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location);
  locationRef.current = location;

  return useMemo(() => {
    const setParams: UrlParamAdapter['setParams'] = (params) => {
      const { pathname, search } = locationRef.current;
      const next = new URLSearchParams(search);
      for (const [k, v] of Object.entries(params)) {
        if (v === '') {
          next.delete(k);
        } else {
          next.set(k, v);
        }
      }
      navigate({ pathname, search: next.toString() }, { replace: true });
    };
    return {
      setParams,
      setParam: (key: string, value: string) => setParams({ [key]: value }),
    };
  }, [navigate]);
}
